import React, { useEffect, useState } from 'react';
import { Download, Eye } from 'lucide-react';
import auditTrailService from '../services/auditTrail';
import { useApps } from '../contexts/AppsContext';
import FeatureOffState, { useFeatureGate } from '../components/common/FeatureOffState';
import { Button } from '../components/ui/Button';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import Modal from '../components/common/Modal';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import useServerTable from '../hooks/useServerTable';

const EMPTY_FACETS = { actions: [], resource_types: [], actors: [] };

const actorName = (e) => e.first_name || e.username || e.actor_user_id || 'system';

const AuditTrailPage = () => {
    const { loading: loadingApps, selectedAppId: appId, selectedApp } = useApps();
    const gate = useFeatureGate(appId);
    const [facets, setFacets] = useState(EMPTY_FACETS);
    const [detailEntry, setDetailEntry] = useState(null);

    // Filter options are per app: reload them on app change.
    useEffect(() => {
        setFacets(EMPTY_FACETS);
        if (!appId) return undefined;
        let cancelled = false;
        auditTrailService
            .getFacets(appId)
            .then((data) => {
                if (cancelled || !data) return;
                setFacets({
                    actions: data.actions || [],
                    resource_types: data.resource_types || [],
                    actors: data.actors || [],
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [appId]);

    // Exports the rows on the current page (as before), in the current sort order.
    const handleExportCSV = () => {
        if (!table.rows.length) return;
        const headers = ['Timestamp', 'Actor', 'Action', 'Resource Type', 'Resource ID', 'IP Address'];
        const rows = table.rows.map((e) => [
            e.created_on,
            `${e.first_name || ''} ${e.last_name || ''} (${e.username || ''})`,
            e.action,
            e.resource_type || '',
            e.resource_id || '',
            e.ip_address || '',
        ]);
        const csv = [
            headers.join(','),
            ...rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns = [
        {
            key: 'created_on',
            header: 'Timestamp',
            sortKey: 'created',
            locked: true,
            className: 'text-slate-500 whitespace-nowrap',
            render: (e) => new Date(e.created_on).toLocaleString(),
            // URL keys: date_from / date_to; the endpoint takes from_date / to_date.
            filter: { key: 'date', label: 'Date', type: 'date', defaultExpanded: true, apiRange: ['filter_from_date', 'filter_to_date'] },
        },
        {
            key: 'actor',
            header: 'Actor',
            sortKey: 'actor',
            render: actorName,
            filter: {
                type: 'multiselect',
                apiKey: 'actor_user_id',
                options: facets.actors.map((a) => ({
                    value: a.user_id,
                    label:
                        a.name && a.username && a.name !== a.username
                            ? `${a.name} (${a.username})`
                            : a.name || a.username || 'Deleted user',
                })),
                emptyText: 'No actors recorded yet.',
            },
        },
        {
            key: 'action',
            header: 'Action',
            sortKey: 'action',
            filter: {
                type: 'multiselect',
                defaultExpanded: true,
                options: facets.actions.map((a) => ({ value: a, label: a })),
                emptyText: 'No actions recorded yet.',
            },
            render: (e) => <span className="px-2 py-0.5 rounded bg-slate-100 text-xs font-medium">{e.action}</span>,
        },
        {
            key: 'resource',
            header: 'Resource',
            sortKey: 'resource',
            className: 'text-slate-500',
            filter: {
                type: 'multiselect',
                apiKey: 'resource_type',
                options: facets.resource_types.map((t) => ({ value: t, label: t })),
                emptyText: 'No resource types recorded yet.',
            },
            render: (e) => (
                <span title={e.resource_id || undefined}>
                    {e.resource_type || '—'}
                    {e.resource_id ? ` / ${e.resource_id.slice(0, 8)}...` : ''}
                </span>
            ),
        },
        {
            key: 'ip_address',
            header: 'IP',
            sortKey: 'ip',
            className: 'text-slate-600 text-xs',
            render: (e) => e.ip_address || '-',
            filter: { key: 'ip', type: 'text', placeholder: 'e.g. 10.0.' },
        },
        {
            key: 'resource_id',
            header: 'Resource ID',
            defaultVisible: false,
            className: 'font-mono text-xs text-slate-500',
            render: (e) => e.resource_id || '-',
        },
        {
            key: 'tenant_id',
            header: 'Tenant',
            defaultVisible: false,
            className: 'font-mono text-xs text-slate-500',
            render: (e) => e.tenant_id || '-',
        },
        {
            key: 'user_agent',
            header: 'User agent',
            defaultVisible: false,
            className: 'max-w-xs truncate text-xs text-slate-500',
            render: (e) => <span title={e.user_agent || undefined}>{e.user_agent || '-'}</span>,
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (e) => (
                <RowActions>
                    <RowAction icon={Eye} label="View details" onClick={() => setDetailEntry(e)} />
                </RowActions>
            ),
        },
    ];

    // URL keys: action, actor, resource, date_from/date_to, ip. The endpoint takes bare
    // names (action, actor_user_id, resource_type, from_date, to_date, ip): see services/auditTrail.
    const table = useServerTable((query) => gate.guard(appId, auditTrailService.getPage(appId, query)), {
        deps: [appId],
        enabled: Boolean(appId),
        defaultSort: { key: 'created', dir: 'desc' },
        columns,
        scopeKey: appId,
        scopedFilters: ['action', 'actor', 'resource'],
    });

    const parsedDetails = (details) => {
        if (typeof details !== 'string') return details;
        try {
            return JSON.parse(details);
        } catch {
            return details;
        }
    };

    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Audit Trail"
                actions={
                    appId &&
                    !gate.off && (
                        <Button variant="outline" onClick={handleExportCSV} disabled={!table.rows.length}>
                            <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
                        </Button>
                    )
                }
            />

            {!appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its audit trail.</p>
                </div>
            ) : gate.off ? (
                <FeatureOffState
                    feature="Audit trail"
                    appName={selectedApp?.app_name}
                    description="Records who changed what in this app — users, roles, settings — with time and IP."
                />
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="audit-trail"
                    rowKey="audit_id"
                    searchPlaceholder="Search action, resource, actor, IP…"
                    emptyMessage="No audit entries found."
                />
            )}

            <Modal isOpen={Boolean(detailEntry)} onClose={() => setDetailEntry(null)} title="Audit Entry" size="md">
                {detailEntry && (
                    <div className="text-sm space-y-1.5">
                        <p>
                            <span className="font-medium">Audit ID:</span> {detailEntry.audit_id}
                        </p>
                        <p>
                            <span className="font-medium">Timestamp:</span>{' '}
                            {new Date(detailEntry.created_on).toLocaleString()}
                        </p>
                        <p>
                            <span className="font-medium">Actor:</span> {actorName(detailEntry)}
                            {detailEntry.actor_user_id ? ` (${detailEntry.actor_user_id})` : ''}
                        </p>
                        <p>
                            <span className="font-medium">Action:</span> {detailEntry.action}
                        </p>
                        <p>
                            <span className="font-medium">Resource:</span> {detailEntry.resource_type || '-'}
                            {detailEntry.resource_id ? ` / ${detailEntry.resource_id}` : ''}
                        </p>
                        <p>
                            <span className="font-medium">IP:</span> {detailEntry.ip_address || '-'}
                        </p>
                        <p>
                            <span className="font-medium">User Agent:</span> {detailEntry.user_agent || '-'}
                        </p>
                        <p>
                            <span className="font-medium">Tenant:</span> {detailEntry.tenant_id || 'N/A'}
                        </p>
                        {detailEntry.details_json && (
                            <div>
                                <span className="font-medium">Details:</span>
                                <pre className="mt-1 p-2 bg-slate-50 border rounded text-xs overflow-x-auto max-h-80">
                                    {JSON.stringify(parsedDetails(detailEntry.details_json), null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AuditTrailPage;
